"use client";
import AnimatedButton, {
  AnimatedButtonProps,
} from "@/commonComponents/animated-button";
import {
  bottomToToptextStagger,
  bottomToToptextStaggerChildren,
} from "@/commonComponents/animations";
import InfoSlides from "@/commonComponents/cards/info-slides";
import SectionWrapper from "@/commonComponents/section-wrapper";
import { Body2, H6_2 } from "@/styles/theme/components/typography.fontvariant";
import { Grid, Stack, Theme, useMediaQuery } from "@mui/material";
import { motion } from "framer-motion";
import { StaticImageData } from "next/image";

export type InTheNewsSectionProps = {
  title: string;
  ButtonProps?: AnimatedButtonProps;
  gridData: {
    image: string | StaticImageData;
    description: string;
    cardHref: string;
    date?: string;
  }[];
  navId?: string;
};

const InTheNewsSection = (props: InTheNewsSectionProps) => {
  const { title, gridData, ButtonProps, navId } = props;
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md"),
  );
  return (
    <SectionWrapper
      SectionHeaderProps={{
        title: title,
      }}
      color={"primary.main"}
      navId={navId || "In-the-news"}
    >
      <Grid
        container
        columnSpacing={3}
        rowSpacing={{ xs: 4, md: 5 }}
        component={motion.div}
        variants={bottomToToptextStagger(0.4)}
        initial={"initial"}
        whileInView={"animate"}
        viewport={{ once: true }}
      >
        {gridData.map((item, index) => {
          const { description, date, image, cardHref } = item;
          return (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={index}
              component={motion.div}
              variants={bottomToToptextStaggerChildren}
            >
              <InfoSlides
                href={cardHref}
                img={image}
                date={date}
                description={description}
                CardWrapperProps={{
                  direction: "column",
                  rowGap: 2,
                  maxWidth: 418.66,
                  sx: {
                    cursor: "pointer",
                    position: "relative",
                    rowGap: 2,
                    columnGap: 3,
                    ".MuiBox-root:after": {
                      display: isMobile ? "none" : "flex",
                      opacity: 0,
                      content: `"View News"`,
                      fontSize: H6_2,
                      color: "white",
                      width: 1,
                      height: 1,
                      position: "absolute",
                      top: 0,
                      left: 0,
                      background:
                        "linear-gradient(0deg, rgba(0, 0, 0, 0.70) 0%, rgba(0, 0, 0, 0.70) 100%)",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      transition: "all .3s",
                    },
                    "&:hover": {
                      ".MuiBox-root:after": {
                        opacity: 1,
                      },
                    },
                  },
                }}
                ImageProps={{
                  width: 420,
                  height: 420,
                  sx: {
                    width: 1,
                    height: { xs: 240, md: "auto" },
                    aspectRatio: { xs: "auto", md: 1 },
                    objectFit: "cover",
                  },
                }}
                TagProps={{
                  fontSize: Body2,
                  bgcolor: "unset",
                  p: 0,
                }}
                DescriptionTypographyProps={{
                  fontSize: H6_2,
                }}
                ContentWrapperProps={{
                  rowGap: 1,
                  flexDirection: "column-reverse",
                }}
              />
            </Grid>
          );
        })}
      </Grid>
      {ButtonProps && ButtonProps.href && ButtonProps.children && (
        <Stack pt={{ xs: 4, md: 7 }} alignItems={"center"}>
          <AnimatedButton {...ButtonProps} />
        </Stack>
      )}
    </SectionWrapper>
  );
};

export default InTheNewsSection;
